/**
 * iter-227 — GuidedDemoTour
 *
 * Floating 5-step click-through overlay that walks a prospect through
 * one coherent buying journey using the seeded flagship record chain:
 *
 *   Requisition PR-2026-000001
 *     ↓
 *   Purchase Order PO-2025-000001 (JSW Steel Limited · Contract CTR-0001)
 *     ↓
 *   Goods Receipt GRN-2026-000001
 *     ↓
 *   Invoice INV-2026-000001
 *     ↓
 *   Analytics wrap-up
 *
 * Each step:
 *   1. Auto-navigates to the correct page (with deep-link params).
 *   2. Highlights the flagship record ID at the top of a floating card.
 *   3. Explains what a real user does at this stage.
 *   4. Offers Next / Back / Exit controls.
 *
 * Persistence: the current step + open state live in localStorage under
 * `procurex_tour` so the tour survives full page reloads triggered by
 * navigation.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Play, ChevronRight, ChevronLeft, X, FileText, Package,
  Truck, Receipt, BarChart3, Sparkles,
} from 'lucide-react';

const STORAGE_KEY = 'procurex_tour_v1';

const STEPS = [
  {
    key: 'pr',
    icon: FileText,
    color: '#EC4899',
    title: 'Step 1 · Requisition',
    subtitle: 'PR-2026-000001',
    body: 'A requesting engineer raises a Purchase Requisition for JSW Steel — the buyer sees AI-enriched line items, budget checks, and one-click PO conversion.',
    highlight: 'PR-2026-000001',
    route: '/app/requisitions?tab=dashboard',
  },
  {
    key: 'po',
    icon: Package,
    color: '#22C55E',
    title: 'Step 2 · Purchase Order',
    subtitle: 'PO-2025-000001 · JSW Steel Limited',
    body: 'The PR converts into a Purchase Order under Contract CTR-0001. Notice the 3-way match preparation, EDI 850 output, and cycle-time analytics running in the background.',
    highlight: 'PO-2025-000001',
    route: '/app/po?tab=workbench',
  },
  {
    key: 'grn',
    icon: Truck,
    color: '#F97316',
    title: 'Step 3 · Goods Receipt',
    subtitle: 'GRN-2026-000001',
    body: 'JSW ships against the PO. The receiving warehouse posts a GRN with quality inspection — variance, ASN & anomaly detection are automated.',
    highlight: 'GRN-2026-000001',
    route: '/app/grn/receipt',
  },
  {
    key: 'inv',
    icon: Receipt,
    color: '#F59E0B',
    title: 'Step 4 · Invoice & Payment',
    subtitle: 'INV-2026-000001',
    body: 'JSW submits an invoice. AI OCR keys the header, the 3-way match engine cross-checks PO + GRN, and payment runs release Net-45 payment automatically.',
    highlight: 'INV-2026-000001',
    route: '/app/invoices?tab=dashboard',
  },
  {
    key: 'analytics',
    icon: BarChart3,
    color: '#8B5CF6',
    title: 'Step 5 · Insights & Analytics',
    subtitle: 'Cross-module wrap-up',
    body: 'Every step above lands in Spend Analytics with vendor + category rollups, so the CPO sees real-time working-capital, maverick spend, and cycle-time from one console.',
    highlight: 'Spend Cube',
    route: '/app/scm/spend-cube',
  },
];

function readState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { open: false, step: 0 };
    const j = JSON.parse(raw);
    return { open: !!j.open, step: Number(j.step) || 0 };
  } catch {
    return { open: false, step: 0 };
  }
}
function writeState(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

export function GuidedDemoTour() {
  const navigate = useNavigate();
  const [{ open, step }, setState] = useState(() => readState());

  // Re-hydrate whenever a step navigates and the component re-mounts.
  useEffect(() => { writeState({ open, step }); }, [open, step]);

  const start = useCallback(() => {
    setState({ open: true, step: 0 });
    navigate(STEPS[0].route);
  }, [navigate]);

  const exit = useCallback(() => {
    setState({ open: false, step: 0 });
  }, []);

  const goto = useCallback((delta) => {
    setState(prev => {
      const nextStep = Math.min(STEPS.length - 1, Math.max(0, prev.step + delta));
      navigate(STEPS[nextStep].route);
      return { open: true, step: nextStep };
    });
  }, [navigate]);

  if (!open) {
    // Compact circular FAB (bottom-left) so it doesn't overlap the
    // chart/dashboard content. Icon-only with tooltip; expands to
    // reveal a short "Tour" label on hover.
    return (
      <button
        type="button"
        onClick={start}
        className="fixed bottom-5 left-5 z-[60] group flex items-center justify-center rounded-full shadow-lg hover:shadow-xl transition-all bg-gradient-to-br from-[#F26B1F] to-[#DB2777] text-white h-12 w-12 hover:w-40 hover:justify-start hover:pl-3.5 overflow-hidden"
        data-testid="guided-tour-launcher"
        title="Take a 5-step guided tour of the platform"
        aria-label="Take the 5-step tour"
      >
        <Sparkles className="w-5 h-5 shrink-0" />
        <span className="ml-2 text-xs font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150">
          5-Step Tour
        </span>
      </button>
    );
  }

  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;
  return (
    <>
      {/* Faint dim so the overlay reads on any background but still
          leaves the page fully clickable. `pointer-events-none` is
          critical — without it the scrim would intercept every click
          on the page, defeating the purpose of a click-through tour. */}
      <div className="fixed inset-0 bg-black/20 backdrop-blur-[1px] z-[55] pointer-events-none" data-testid="guided-tour-scrim" />
      <div
        className="fixed bottom-5 left-5 w-[380px] max-w-[92vw] z-[60] rounded-xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
        data-testid="guided-tour-card"
      >
        {/* Header */}
        <div
          className="px-4 py-3 text-white flex items-center gap-2"
          style={{ background: `linear-gradient(135deg, ${s.color}, ${s.color}CC)` }}
        >
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold opacity-90">{s.title}</div>
            <div className="text-[11px] opacity-80 truncate">{s.subtitle}</div>
          </div>
          <button onClick={exit} className="opacity-70 hover:opacity-100 transition-opacity"
                   data-testid="guided-tour-close" title="Exit tour">
            <X className="w-4 h-4" />
          </button>
        </div>
        {/* Body */}
        <div className="p-4 space-y-3">
          <div className="text-[13px] text-slate-700 leading-snug">{s.body}</div>
          <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
            <span className="font-semibold">Highlighted record:</span>
            <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 font-mono text-[10px]"
                  data-testid="guided-tour-highlight">
              {s.highlight}
            </code>
          </div>
        </div>
        {/* Progress bar */}
        <div className="px-4 pb-1">
          <div className="flex items-center gap-1">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className="flex-1 h-1 rounded-full transition-colors"
                style={{ background: i <= step ? s.color : '#E2E8F0' }}
              />
            ))}
          </div>
          <div className="text-[10px] text-slate-500 mt-1 font-medium">
            Step {step + 1} of {STEPS.length}
          </div>
        </div>
        {/* Footer */}
        <div className="px-4 py-3 flex items-center justify-between gap-2 border-t border-slate-100 bg-slate-50/50">
          <Button
            variant="outline" size="sm" className="h-8 text-xs"
            onClick={() => goto(-1)} disabled={step === 0}
            data-testid="guided-tour-back"
          >
            <ChevronLeft className="w-3.5 h-3.5 mr-1" />
            Back
          </Button>
          {isLast ? (
            <Button
              size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-500"
              onClick={exit}
              data-testid="guided-tour-finish"
            >
              Finish Tour
            </Button>
          ) : (
            <Button
              size="sm" className="h-8 text-xs"
              style={{ background: s.color }}
              onClick={() => goto(1)}
              data-testid="guided-tour-next"
            >
              Next
              <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

export default GuidedDemoTour;
